import { Link } from '@tanstack/react-router';
import { type AlertDto, severityName } from '@vision360/contracts';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { absoluteTime, ago } from '@/widgets/_sdk/format';
import { useSources } from '@/widgets/_sdk/hooks';
import { AlertActions, canAck } from './alert-actions';
import { AlertDetail } from './alert-detail';
import { useAlertPages, useBulkAck, useNoisiestRules } from './api';
import { StatusChip } from './status-chip';

/**
 * Alerts (PRD VS-061…063): filter by state, acknowledge, snooze, resolve — one
 * by one or the selection at once — the detail with its timeline, and the
 * noisiest rules of the week. Live updates arrive through the stream (`['alerts']`).
 */

const FILTERS = ['open', 'acknowledged', 'snoozed', 'resolved', 'all'] as const;
type Filter = (typeof FILTERS)[number];

/** Severity as a rail on the row's edge; the word is in the meta line too (never colour alone). */
const SEVERITY_RAIL: Record<AlertDto['severity'], string> = {
  0: 'bg-muted-foreground/40',
  1: 'bg-emerald-500',
  2: 'bg-amber-500',
  3: 'bg-red-500',
  4: 'bg-red-600 shadow-[0_0_10px_var(--color-red-600)]',
};

const CONTROL = 'h-9 pointer-coarse:h-11';
const STAGGER_MS = 30;
const STAGGER_CAP = 10;

function StatusFilter({ value, onChange }: { value: Filter; onChange: (f: Filter) => void }) {
  const { t } = useTranslation('alerts');
  const name = useId();
  return (
    <fieldset className="-mx-1 max-w-full overflow-x-auto px-1 py-1">
      <legend className="sr-only">{t('filter.label')}</legend>
      <div className="relative grid min-w-[34rem] grid-cols-5 rounded-lg bg-muted/70 p-1">
        {/* The selection slides to the chosen state: it shows what the list now holds. */}
        <span
          aria-hidden="true"
          className="absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/5)] rounded-md bg-card shadow-sm transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
          style={{ transform: `translateX(${FILTERS.indexOf(value) * 100}%)` }}
        >
          <span className="absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-primary" />
        </span>
        {FILTERS.map((f) => (
          <label
            key={f}
            className={cn(
              CONTROL,
              'relative flex cursor-pointer items-center justify-center rounded-md px-3 text-sm font-medium whitespace-nowrap transition-colors duration-150',
              'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring',
              f === value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <input
              type="radio"
              name={name}
              value={f}
              checked={f === value}
              onChange={() => onChange(f)}
              className="sr-only"
            />
            {t(`filter.${f}`)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function AlertRow({
  alert,
  meta,
  index,
  selected,
  onSelect,
  onOpen,
  now,
}: {
  alert: AlertDto;
  meta: string;
  index: number;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onOpen: () => void;
  now: number;
}) {
  const { t } = useTranslation(['alerts', 'widgets']);
  const resolved = alert.status === 'resolved';
  return (
    <li
      className={cn(
        'relative grid animate-rise grid-cols-[2.75rem_minmax(0,1fr)] gap-x-2 gap-y-2 py-3 pr-4 pl-2 transition-colors duration-150 md:grid-cols-[2.75rem_minmax(0,1fr)_auto]',
        selected && 'bg-primary/6',
      )}
      style={{ animationDelay: `${Math.min(index, STAGGER_CAP) * STAGGER_MS}ms` }}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-y-3 left-0 w-[3px] rounded-r-full',
          SEVERITY_RAIL[alert.severity],
          resolved && 'opacity-40',
        )}
      />
      <div className="flex justify-center">
        {canAck(alert) && (
          <label className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-md hover:bg-muted">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={selected}
              aria-label={t('alerts:select.one', { title: alert.title })}
              onChange={(e) => onSelect(e.target.checked)}
            />
          </label>
        )}
      </div>

      <div className="min-w-0 space-y-1 pt-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={onOpen}
            className={cn(
              'rounded-sm text-left leading-snug font-medium underline-offset-4 hover:underline',
              resolved && 'text-muted-foreground',
            )}
          >
            {alert.title}
          </button>
          {alert.fireCount > 1 && (
            <span className="rounded-md bg-muted px-1.5 font-mono text-xs leading-5 tabular-nums">
              <span aria-hidden="true">{t('alerts:fireCount', { count: alert.fireCount })}</span>
              <span className="sr-only">
                {t('alerts:fireCountLabel', { count: alert.fireCount })}
              </span>
            </span>
          )}
        </div>
        <p className="font-mono text-xs text-muted-foreground">
          {t(`widgets:severity.${severityName(alert.severity)}`)}
          {meta && ` · ${meta}`}
          {' · '}
          <time dateTime={alert.openedAt} title={absoluteTime.format(new Date(alert.openedAt))}>
            {t('alerts:openedAgo', { ago: ago(alert.openedAt, now) })}
          </time>
        </p>
      </div>

      <div className="col-start-2 flex flex-col items-start gap-2 md:col-start-3 md:items-end md:pt-2">
        <StatusChip alert={alert} />
        <AlertActions alert={alert} className="md:[&>div]:justify-end" />
      </div>
    </li>
  );
}

function NoisiestPanel() {
  const { t } = useTranslation('alerts');
  const noisy = useNoisiestRules(7);
  const titleId = useId();
  if (noisy.isPending) return null;
  if (noisy.isError) {
    return <p className="glass p-4 text-sm text-muted-foreground">{t('noisy.error')}</p>;
  }
  const { rules, from, to } = noisy.data;
  if (rules.length === 0) return null;
  const max = Math.max(1, ...rules.map((r) => r.fires));
  const day = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });

  return (
    <aside aria-labelledby={titleId} className="glass flex animate-rise flex-col gap-4 p-5">
      <div className="space-y-1">
        <h2 id={titleId} className="font-semibold tracking-tight">
          {t('noisy.title')}
        </h2>
        <p className="text-xs leading-relaxed text-muted-foreground">{t('noisy.description')}</p>
      </div>
      <ol className="space-y-3.5">
        {rules.map((rule, i) => (
          <li key={rule.ruleId} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span
                className={cn(
                  'truncate text-sm font-medium',
                  !rule.ruleName && 'text-muted-foreground italic',
                )}
              >
                {rule.ruleName ?? t('deletedRule')}
              </span>
              <span className="shrink-0 font-mono text-xs tabular-nums">
                {t('noisy.fires', { count: rule.fires })}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full origin-left animate-grow rounded-full bg-primary"
                style={{
                  width: `${(rule.fires / max) * 100}%`,
                  animationDelay: `${Math.min(i, STAGGER_CAP) * STAGGER_MS}ms`,
                }}
              />
            </div>
            <p className="font-mono text-xs text-muted-foreground tabular-nums">
              {t('noisy.alerts', { count: rule.alerts })}
            </p>
          </li>
        ))}
      </ol>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-xs">
        <span className="font-mono text-muted-foreground">
          {t('noisy.window', {
            from: day.format(new Date(from)),
            to: day.format(new Date(to)),
          })}
        </span>
        <Link
          to="/rules"
          className="rounded-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {t('noisy.link')}
        </Link>
      </footer>
    </aside>
  );
}

export function AlertsPage({ now = Date.now }: { now?: () => number }) {
  const { t } = useTranslation(['alerts', 'widgets']);
  const [filter, setFilter] = useState<Filter>('open');
  const pages = useAlertPages(filter === 'all' ? undefined : filter);
  const sources = useSources();
  const bulk = useBulkAck();
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  // A snapshot: the dialog stays open when its alert leaves the filtered list.
  const [opened, setOpened] = useState<AlertDto | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const alerts = pages.data?.pages.flatMap((p) => p.alerts) ?? [];
  const ackable = alerts.filter(canAck);
  // Only what is still on screen and still acknowledgeable counts as selected.
  const selected = ackable.filter((a) => picked.has(a.id)).map((a) => a.id);
  const allSelected = ackable.length > 0 && selected.length === ackable.length;
  const current = now();

  const sourceName = (id: string | null) => {
    if (!id || !sources.data) return null;
    return sources.data.sources.find((s) => s.id === id)?.name ?? t('alerts:unknownSource');
  };
  const metaOf = (a: AlertDto) =>
    [a.ruleName ?? t('alerts:deletedRule'), sourceName(a.sourceId)].filter(Boolean).join(' · ');

  const select = (ids: string[], on: boolean) => {
    setDone(null);
    setPicked((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const ackSelected = () =>
    bulk.mutate(selected, {
      onSuccess: (count) => {
        setPicked(new Set());
        setDone(count);
      },
    });

  let body: React.ReactNode;
  if (pages.isPending) {
    body = (
      <output aria-live="polite" className="flex items-center gap-2 p-6 text-muted-foreground">
        <Spinner />
        {t('alerts:loading')}
      </output>
    );
  } else if (pages.isError) {
    body = (
      <div className="p-4">
        <Alert variant="destructive" role="alert">
          {t('alerts:error')}
        </Alert>
      </div>
    );
  } else if (alerts.length === 0) {
    body = (
      <div className="flex flex-col items-start gap-3 p-6">
        <p className="max-w-prose text-muted-foreground">
          {filter === 'open' ? t('alerts:empty.open') : t('alerts:empty.other')}
        </p>
        {filter === 'open' && (
          <Link
            to="/rules"
            className="rounded-sm text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('alerts:empty.rules')}
          </Link>
        )}
      </div>
    );
  } else {
    body = (
      <>
        {ackable.length > 0 && (
          <div className="sticky top-0 z-10 flex min-h-14 flex-wrap items-center gap-x-4 gap-y-2 rounded-t-[14px] border-b bg-card/85 py-1 pr-4 pl-2 backdrop-blur-md">
            <label className="inline-flex cursor-pointer items-center gap-1 text-sm">
              <span className="inline-flex h-11 w-11 items-center justify-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selected.length > 0 && !allSelected;
                  }}
                  onChange={(e) =>
                    select(
                      ackable.map((a) => a.id),
                      e.target.checked,
                    )
                  }
                />
              </span>
              {t('alerts:select.all')}
            </label>
            {selected.length > 0 && (
              <div className="ml-auto flex animate-rise flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground tabular-nums">
                  {t('alerts:select.count', { count: selected.length })}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className={CONTROL}
                  onClick={() => setPicked(new Set())}
                >
                  {t('alerts:select.clear')}
                </Button>
                <Button
                  size="sm"
                  className={CONTROL}
                  disabled={bulk.isPending}
                  onClick={ackSelected}
                >
                  {bulk.isPending && <Spinner />}
                  {bulk.isPending
                    ? t('alerts:select.pending')
                    : t('alerts:select.ack', { count: selected.length })}
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Keyed by filter: a new filter re-runs the short cascade; live arrivals rise alone. */}
        <ul key={filter} className="divide-y">
          {alerts.map((alert, i) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              meta={metaOf(alert)}
              index={i}
              now={current}
              selected={picked.has(alert.id)}
              onSelect={(on) => select([alert.id], on)}
              onOpen={() => setOpened(alert)}
            />
          ))}
        </ul>
        {pages.hasNextPage && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              className={cn(CONTROL, 'w-full')}
              disabled={pages.isFetchingNextPage}
              onClick={() => void pages.fetchNextPage()}
            >
              {pages.isFetchingNextPage && <Spinner />}
              {pages.isFetchingNextPage ? t('alerts:loadingMore') : t('alerts:loadMore')}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="max-w-prose space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('alerts:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('alerts:description')}</p>
      </div>

      <StatusFilter
        value={filter}
        onChange={(f) => {
          setFilter(f);
          setDone(null);
        }}
      />

      <output aria-live="polite" className="block text-sm empty:hidden">
        {bulk.isError
          ? t('alerts:select.failed')
          : done !== null
            ? t('alerts:select.done', { count: done })
            : ''}
      </output>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section aria-label={t(`alerts:filter.${filter}`)} className="glass overflow-clip">
          {body}
          {pages.isSuccess && alerts.length > 0 && (
            <p className="border-t px-4 py-2 font-mono text-xs text-muted-foreground tabular-nums">
              {t('alerts:updated', {
                time: absoluteTime.format(new Date(pages.dataUpdatedAt)),
              })}
            </p>
          )}
        </section>
        <NoisiestPanel />
      </div>

      {opened && (
        <AlertDetail
          alert={opened}
          meta={metaOf(opened)}
          now={current}
          onClose={() => setOpened(null)}
        />
      )}
    </div>
  );
}
